export class ApiContractError extends Error {
  constructor(endpoint, expected, actual) {
    super(`Некорректный ответ ${endpoint}: ожидался ${expected}, получен ${actual}`);
    this.name = "ApiContractError";
    this.endpoint = endpoint;
  }
}

const describe = (value) => {
  if (value === null) return "null";
  if (Array.isArray(value)) return "array";
  return typeof value;
};

export function normalizeListResponse(value, endpoint) {
  if (Array.isArray(value)) return value;
  if (value && typeof value === "object" && !Array.isArray(value) && Array.isArray(value.items)) {
    const rows = [...value.items];
    const { items, ...metadata } = value;
    Object.defineProperty(rows, "responseMetadata", { value: metadata, enumerable: false });
    return rows;
  }
  throw new ApiContractError(endpoint, "массив или объект { items: массив }", describe(value));
}

function normalizeAppRows(value, endpoint) {
  return normalizeListResponse(value, endpoint).map((app, index) => {
    if (!app || typeof app !== "object" || Array.isArray(app) || typeof app.id !== "string" || typeof app.name !== "string") {
      throw new ApiContractError(`${endpoint}[${index}]`, "объект приложения с id и name", describe(app));
    }
    if (app.screenshots != null && !Array.isArray(app.screenshots)) {
      throw new ApiContractError(`${endpoint}[${index}].screenshots`, "массив", describe(app.screenshots));
    }
    return app;
  });
}

function normalizeCollectionRows(value, endpoint) {
  return normalizeListResponse(value, endpoint).map((collection, index) => {
    if (!collection || typeof collection !== "object" || Array.isArray(collection)) {
      throw new ApiContractError(`${endpoint}[${index}]`, "объект подборки", describe(collection));
    }
    if (!("apps" in collection)) {
      throw new ApiContractError(`${endpoint}[${index}].apps`, "массив", "отсутствующее поле");
    }
    return { ...collection, apps: normalizeAppRows(collection.apps, `${endpoint}[${index}].apps`) };
  });
}

export function normalizeObjectListResponse(value, endpoint) {
  const rows = normalizeListResponse(value, endpoint);
  rows.forEach((row, index) => {
    if (!row || typeof row !== "object" || Array.isArray(row)) {
      throw new ApiContractError(`${endpoint}[${index}]`, "объект", describe(row));
    }
  });
  return rows;
}

export function normalizeBlockedUserListResponse(value, endpoint) {
  return normalizeObjectListResponse(value, endpoint).map((person, index) => {
    for (const key of ["sources", "active_requests"]) {
      if (key in person && !Array.isArray(person[key])) {
        throw new ApiContractError(`${endpoint}[${index}].${key}`, "массив", describe(person[key]));
      }
    }
    return person;
  });
}

export function normalizeAdminLogsResponse(value) {
  if (!value || typeof value !== "object" || Array.isArray(value)) {
    throw new ApiContractError("/admin/logs", "объект { items, stats }", describe(value));
  }
  if (!value.stats || typeof value.stats !== "object" || Array.isArray(value.stats)) {
    throw new ApiContractError("/admin/logs.stats", "объект", describe(value.stats));
  }
  return { ...value, items: normalizeObjectListResponse(value.items, "/admin/logs.items") };
}

export function normalizeCatalogResponse(value) {
  if (!value || typeof value !== "object" || Array.isArray(value)) {
    throw new ApiContractError("/catalog", "объект каталога", describe(value));
  }
  const requiredLists = ["featured", "popular", "collections", "categories"];
  const normalized = { ...value };
  for (const key of requiredLists) {
    if (!(key in value)) {
      throw new ApiContractError(`/catalog.${key}`, "массив", "отсутствующее поле");
    }
    if (key === "collections") normalized[key] = normalizeCollectionRows(value[key], `/catalog.${key}`);
    else if (key === "featured" || key === "popular") normalized[key] = normalizeAppRows(value[key], `/catalog.${key}`);
    else normalized[key] = normalizeObjectListResponse(value[key], `/catalog.${key}`);
  }
  return normalized;
}

export const normalizeAppListResponse = (value, endpoint) => normalizeAppRows(value, endpoint);
export const normalizeCollectionListResponse = (value, endpoint) => normalizeCollectionRows(value, endpoint);
export function normalizeAppResponse(value, endpoint) {
  return normalizeAppRows([value], endpoint)[0];
}

export function normalizeEntityResponse(value, endpoint) {
  if (!value || typeof value !== "object" || Array.isArray(value)) {
    throw new ApiContractError(endpoint, "объект", describe(value));
  }
  return value;
}

export function apiErrorMessage(error) {
  if (error?.name === "ApiContractError") return "Сервер вернул данные в неожиданном формате.";
  if (error?.response?.status === 404) return "Приложение не найдено.";
  if (error?.response) return `Не удалось загрузить данные (HTTP ${error.response.status}).`;
  if (error?.request) return "Не удалось связаться с сервером. Проверьте интернет и попробуйте снова.";
  return "Не удалось загрузить данные. Попробуйте ещё раз.";
}
